import { create } from "zustand";
import { NavigateFunction } from "react-router-dom";
import { IgnoreZone } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import {
  IgnoreZoneAssignment,
  IgnoreZoneMetricOption,
  NewIgnoreZonesEditSession,
} from "../types";
import { apiIgnoreZonesToAssignments } from "../utils/apiIgnoreZonesToAssignments";
import {
  buildAvailableMetrics,
  getAllMetricIds,
} from "../utils/assignmentMetricLabels";
import {
  fetchNewIgnoreZonesPageData,
  isFetchError,
} from "../utils/fetchNewIgnoreZonesPageData";
import { getNextAssignmentColor } from "../utils/getNextAssignmentColor";
import {
  formatZoneName,
  getNextZoneNumber,
  resolveZoneName,
  ensureAssignmentNames,
} from "../utils/zoneNames";
import {
  transformNormalizedToPixelCoordinates,
  transformPixelToNormalizedCoordinates,
} from "../utils/coordinateTransforms";
import { universalAPIErrorHandler } from "../../../utils/universalAPIErrorHandler";

const createLocalId = (): string =>
  `assignment-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const cloneAssignment = (
  assignment: IgnoreZoneAssignment
): IgnoreZoneAssignment => JSON.parse(JSON.stringify(assignment));

const cloneEditSession = (
  session: NewIgnoreZonesEditSession
): NewIgnoreZonesEditSession => JSON.parse(JSON.stringify(session));

const emptyEditSession = (): NewIgnoreZonesEditSession => ({
  localId: "",
  name: "",
  color: "",
  coordinates: [],
  metricIds: [],
  isIgnoreAll: false,
  isNew: false,
});

interface NewIgnoreZonesStore {
  pageLoading: boolean;
  breadcrumbs: string[];
  cameraName: string;
  previewUrl: string;
  dimensions: { width: number; height: number };
  adjustedDimensions: { x: number; y: number };
  apiRows: IgnoreZone[];
  availableMetrics: IgnoreZoneMetricOption[];
  assignments: IgnoreZoneAssignment[];
  editorVisible: boolean;
  selectedAssignmentLocalId: string | null;
  editSession: NewIgnoreZonesEditSession | null;
  editSessionSnapshot: NewIgnoreZonesEditSession | null;
  shapesArray: number[][][];
  editsMade: boolean;
  saveValidationError: string;

  resetStore: () => void;
  getNewIgnoreZonesPageInfo: (navigate: NavigateFunction) => Promise<void>;
  openEditAssignment: (localId: string) => void;
  openNewAssignment: () => void;
  updateEditSessionColor: (color: string) => void;
  updateEditSessionName: (name: string) => void;
  setEditSessionIgnoreAll: (checked: boolean) => void;
  toggleEditSessionMetric: (metricId: number, checked: boolean) => void;
  setShapesArray: (shapes: number[][][]) => void;
  saveEditSession: () => boolean;
  cancelEditSession: () => void;
  deleteAssignment: (localId: string) => void;
  getUsedColors: (excludeLocalId?: string) => string[];
}

const initialState = {
  pageLoading: true,
  breadcrumbs: [] as string[],
  cameraName: "",
  previewUrl: "",
  dimensions: { width: 640, height: 360 },
  adjustedDimensions: { x: 720, y: 576 },
  apiRows: [] as IgnoreZone[],
  availableMetrics: [] as IgnoreZoneMetricOption[],
  assignments: [] as IgnoreZoneAssignment[],
  editorVisible: false,
  selectedAssignmentLocalId: null as string | null,
  editSession: null as NewIgnoreZonesEditSession | null,
  editSessionSnapshot: null as NewIgnoreZonesEditSession | null,
  shapesArray: [] as number[][][],
  editsMade: false,
  saveValidationError: "",
};

export const useNewIgnoreZonesStore = create<NewIgnoreZonesStore>((set, get) => ({
  ...initialState,

  resetStore: () => set({ ...initialState }),

  getNewIgnoreZonesPageInfo: async (navigate) => {
    set({ pageLoading: true, saveValidationError: "" });
    const retry = () => {
      void get().getNewIgnoreZonesPageInfo(navigate);
    };

    try {
      const data = await fetchNewIgnoreZonesPageData();
      const availableMetrics = buildAvailableMetrics(data.apiRows);
      const assignments = ensureAssignmentNames(
        apiIgnoreZonesToAssignments(data.apiRows)
      );

      set({
        pageLoading: false,
        previewUrl: data.previewUrl,
        cameraName: data.cameraName,
        dimensions: data.dimensions,
        adjustedDimensions: data.adjustedDimensions,
        breadcrumbs: data.breadcrumbs,
        apiRows: data.apiRows,
        availableMetrics,
        assignments,
        editorVisible: false,
        selectedAssignmentLocalId: null,
        editSession: null,
        editSessionSnapshot: null,
        shapesArray: [],
        editsMade: false,
        saveValidationError: "",
      });
    } catch (error) {
      set({ pageLoading: false });
      if (isFetchError(error)) {
        universalAPIErrorHandler(error, navigate, retry, () => {
          alert("Error loading ignore zones, please try again");
        });
      } else {
        console.error(error);
        alert("Error loading ignore zones, please try again");
      }
    }
  },

  openEditAssignment: (localId) => {
    const assignment = get().assignments.find((a) => a.localId === localId);
    if (!assignment) {
      return;
    }

    const { adjustedDimensions } = get();
    const assignmentIndex = get().assignments.findIndex(
      (a) => a.localId === localId
    );
    const session: NewIgnoreZonesEditSession = {
      localId: assignment.localId,
      name:
        assignment.name?.trim() ||
        formatZoneName(Math.max(assignmentIndex + 1, 1)),
      color: assignment.color,
      coordinates: cloneAssignment(assignment).coordinates,
      metricIds: [...assignment.metricIds],
      isIgnoreAll: assignment.isIgnoreAll,
      isNew: false,
    };

    const shapesArray = transformNormalizedToPixelCoordinates(
      session.coordinates,
      adjustedDimensions.x,
      adjustedDimensions.y
    );

    set({
      editorVisible: true,
      selectedAssignmentLocalId: localId,
      editSession: session,
      editSessionSnapshot: cloneEditSession(session),
      shapesArray,
      saveValidationError: "",
    });
  },

  openNewAssignment: () => {
    const { assignments } = get();
    const usedColors = assignments.map((a) => a.color);
    const color = getNextAssignmentColor(usedColors);
    const localId = createLocalId();

    const session: NewIgnoreZonesEditSession = {
      localId,
      name: formatZoneName(getNextZoneNumber(assignments)),
      color,
      coordinates: [],
      metricIds: [],
      isIgnoreAll: false,
      isNew: true,
    };

    set({
      editorVisible: true,
      selectedAssignmentLocalId: localId,
      editSession: session,
      editSessionSnapshot: cloneEditSession(session),
      shapesArray: [],
      saveValidationError: "",
    });
  },

  updateEditSessionColor: (color) => {
    const { editSession } = get();
    if (!editSession) {
      return;
    }
    set({
      editSession: { ...editSession, color },
      editsMade: true,
      saveValidationError: "",
    });
  },

  updateEditSessionName: (name) => {
    const { editSession } = get();
    if (!editSession) {
      return;
    }
    set({
      editSession: { ...editSession, name },
      editsMade: true,
      saveValidationError: "",
    });
  },

  setEditSessionIgnoreAll: (checked) => {
    const { editSession, availableMetrics } = get();
    if (!editSession) {
      return;
    }

    const allMetricIds = getAllMetricIds(availableMetrics);
    set({
      editSession: {
        ...editSession,
        isIgnoreAll: checked,
        metricIds: checked ? allMetricIds : [],
      },
      editsMade: true,
      saveValidationError: "",
    });
  },

  toggleEditSessionMetric: (metricId, checked) => {
    const { editSession, availableMetrics } = get();
    if (!editSession) {
      return;
    }

    let metricIds = [...editSession.metricIds];
    if (checked) {
      if (!metricIds.includes(metricId)) {
        metricIds.push(metricId);
      }
    } else {
      metricIds = metricIds.filter((id) => id !== metricId);
    }

    const allMetricIds = getAllMetricIds(availableMetrics);
    const isIgnoreAll =
      allMetricIds.length > 0 &&
      allMetricIds.every((id) => metricIds.includes(id));

    set({
      editSession: {
        ...editSession,
        metricIds,
        isIgnoreAll,
      },
      editsMade: true,
      saveValidationError: "",
    });
  },

  setShapesArray: (shapesArray) => {
    set({ shapesArray, editsMade: true, saveValidationError: "" });
  },

  saveEditSession: () => {
    const {
      editSession,
      shapesArray,
      assignments,
      adjustedDimensions,
      availableMetrics,
    } = get();

    if (!editSession) {
      return false;
    }

    const hasMetrics =
      editSession.isIgnoreAll || editSession.metricIds.length > 0;
    if (!hasMetrics) {
      set({
        saveValidationError:
          "Select at least one product or Ignore All before saving.",
      });
      return false;
    }

    const normalizedCoordinates = transformPixelToNormalizedCoordinates(
      shapesArray,
      adjustedDimensions.x,
      adjustedDimensions.y
    );

    if (normalizedCoordinates.length === 0) {
      set({
        saveValidationError: "Draw at least one ignore zone shape before saving.",
      });
      return false;
    }

    const existingIndex = assignments.findIndex(
      (a) => a.localId === editSession.localId
    );

    const updatedAssignment: IgnoreZoneAssignment = {
      localId: editSession.localId,
      name: resolveZoneName(editSession.name, assignments, editSession.localId),
      color: editSession.color,
      coordinates: normalizedCoordinates,
      metricIds: editSession.isIgnoreAll
        ? getAllMetricIds(availableMetrics)
        : [...editSession.metricIds],
      isIgnoreAll: editSession.isIgnoreAll,
      sourceApiLabels:
        existingIndex >= 0
          ? assignments[existingIndex].sourceApiLabels
          : [],
    };

    const nextAssignments = [...assignments];
    if (existingIndex >= 0) {
      nextAssignments[existingIndex] = updatedAssignment;
    } else {
      nextAssignments.push(updatedAssignment);
    }

    set({
      assignments: nextAssignments,
      editorVisible: false,
      selectedAssignmentLocalId: null,
      editSession: null,
      editSessionSnapshot: null,
      shapesArray: [],
      editsMade: true,
      saveValidationError: "",
    });
    return true;
  },

  cancelEditSession: () => {
    set({
      editorVisible: false,
      selectedAssignmentLocalId: null,
      editSession: null,
      editSessionSnapshot: null,
      shapesArray: [],
      saveValidationError: "",
    });
  },

  deleteAssignment: (localId) => {
    const { assignments, selectedAssignmentLocalId, editorVisible } = get();
    const nextAssignments = assignments.filter(
      (assignment) => assignment.localId !== localId
    );

    if (editorVisible && selectedAssignmentLocalId === localId) {
      set({
        assignments: nextAssignments,
        editorVisible: false,
        selectedAssignmentLocalId: null,
        editSession: null,
        editSessionSnapshot: null,
        shapesArray: [],
        editsMade: true,
        saveValidationError: "",
      });
      return;
    }

    set({
      assignments: nextAssignments,
      editsMade: true,
    });
  },

  getUsedColors: (excludeLocalId) => {
    const { assignments } = get();
    return assignments
      .filter((a) => a.localId !== excludeLocalId)
      .map((a) => a.color);
  },
}));
